// src/services/authService.ts

import { api } from './api';

export interface LoginDto {
  email:    string;
  password: string;
}

export interface User {
  _id:                string;
  email:              string;
  name:               string;
  role:               string;
  roleId?:            string;
  department?:        string;
  isAdmin?:           boolean;
  mustResetPassword?: boolean;
  avatar?:            string;
  lastLogin?:         string;
  mobile?:            string;
  isEmailVerified?:   boolean;
  isMobileVerified?:  boolean;
}

export interface AuthResponse {
  accessToken: string;
  expiresIn:   number;    // seconds until access token expires (24 * 60 * 60 = 86400)
  user:        User;
}

// login() returns this instead of AuthResponse when the account has MFA
// enabled — no tokens/cookie are issued yet, just a short-lived challenge.
export interface MfaChallengeResponse {
  mfaRequired: true;
  mfaToken: string;
}

// login() returns this instead of AuthResponse when REQUIRE_EMAIL_VERIFICATION
// is on and the account hasn't verified its email yet — no tokens issued.
export interface EmailVerificationRequiredResponse {
  emailVerificationRequired: true;
  email: string;
}

export interface MfaSetupResponse {
  secret: string;
  otpauthUrl: string;
  qrCode: string;   // data: URL, ready for an <img src>
}

export interface RefreshResponse {
  accessToken: string;
  expiresIn:   number;
}

export interface Session {
  _id:       string;
  tokenId:   string;
  userAgent?: string;
  ipAddress?: string;
  createdAt: string;
  expiresAt: string;
}

export const authService = {
  // POST /api/auth/login — may return an MFA challenge, or an
  // email-verification-required response, instead of tokens
  login: (dto: LoginDto) =>
    api.post<AuthResponse | MfaChallengeResponse | EmailVerificationRequiredResponse>('/auth/login', dto).then(r => r.data),

  // POST /api/auth/mfa/verify-login — second step of an MFA login
  verifyLoginMfa: (mfaToken: string, code: string) =>
    api.post<AuthResponse>('/auth/mfa/verify-login', { mfaToken, code }).then(r => r.data),

  // POST /api/auth/mfa/setup — generates a pending secret + QR code
  mfaSetup: () =>
    api.post<MfaSetupResponse>('/auth/mfa/setup').then(r => r.data),

  // POST /api/auth/mfa/verify-setup — confirms setup, returns backup codes once
  mfaVerifySetup: (code: string) =>
    api.post<{ message: string; backupCodes: string[] }>('/auth/mfa/verify-setup', { code }).then(r => r.data),

  // POST /api/auth/mfa/disable
  mfaDisable: (password: string, code: string) =>
    api.post('/auth/mfa/disable', { password, code }).then(r => r.data),

  // GET /api/auth/mfa/status
  mfaStatus: () =>
    api.get<{ enabled: boolean }>('/auth/mfa/status').then(r => r.data),

  // POST /api/auth/refresh  — browser sends httpOnly cookie automatically
  refresh: () =>
    api.post<RefreshResponse>('/auth/refresh').then(r => r.data),

  // POST /api/auth/logout
  logout: () =>
    api.post('/auth/logout').then(r => r.data),

  // POST /api/auth/logout-all
  logoutAll: () =>
    api.post('/auth/logout-all').then(r => r.data),

  // GET /api/auth/me
  me: () =>
    api.get<User>('/auth/me').then(r => r.data),

  // GET /api/auth/sessions
  getSessions: () =>
    api.get<Session[]>('/auth/sessions').then(r => r.data),

  // DELETE /api/auth/sessions/:tokenId — revoke one specific session,
  // distinct from logoutAll (which revokes every session at once).
  revokeSession: (tokenId: string) =>
    api.delete(`/auth/sessions/${tokenId}`).then(r => r.data),

  // PATCH /api/auth/change-password
  changePassword: (currentPassword: string, newPassword: string) =>
    api.patch('/auth/change-password', { currentPassword, newPassword }).then(r => r.data),

  // POST /api/auth/verify-email
  verifyEmail: (token: string) =>
    api.post<{ message: string }>('/auth/verify-email', { token }).then(r => r.data),

  // POST /api/auth/verify-email-change — confirms a pending email change
  verifyEmailChange: (token: string) =>
    api.post<{ message: string }>('/auth/verify-email-change', { token }).then(r => r.data),

  // POST /api/auth/resend-verification
  resendVerification: (email: string) =>
    api.post<{ message: string }>('/auth/resend-verification', { email }).then(r => r.data),
};
